import { FaLock, FaMagnifyingGlass, FaPen, FaPlus, FaRegCopy, FaTrash, FaTrashCan } from "react-icons/fa6";
import backgroundImage from "../../assets/profile_image_bg.jpg";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import FormLoading from "../FormLoading";
import { useModal } from "../../providers/ModalProvider";
import { copyToClipBoard, formatCurrency, parseMoneyToCents } from "../../utils/functions";
import { getVIACEPAddressByCEP } from '../../helpers/address_api_requests';
import { Controller, useForm } from 'react-hook-form';
import { Avatar, Box, Button, Card, Checkbox, Chip, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, Button as MuiButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { DateTimePicker } from '@mui/x-date-pickers';
import { currentDate, defaultDeliveryDate as fallbackDeliveryDate, stepNames as fallbackStepNames } from '../../utils/data_variables';
import { getProductionSteps, mapProductionStepNames } from '../../helpers/http_production_steps_requests';
import { useCatalog } from '../../providers/CatalogProvider';
import dayjs from 'dayjs';
import { TextMaskCustom, CurrencyMaskCustom, PhoneMaskCustom, CpfCnpjMaskCustom, ZipCodeMaskCustom } from './CustomFormPage/MaskInputs';
import FormPageNavigation from './CustomFormPage/FormPageNavigation';

export default function CustomFormPage({
    details,
    initialData,
    onSubmit,
    onCancel,
    formFieldList = [],
    avatarUpload,
    multiImageUpload,
    isLoading,
    submitButtonText = "Salvar",
    cancelButtonText = "Cancelar",
    fixedActions,
    production,
    updateProduction,
    noAction,
    verification
}) {

    const { handleOpen, setImage, setIsImage } = useModal();
    const { defaultDeliveryDate: catalogDeliveryDate, presets = [] } = useCatalog();
    const defaultDeliveryDate = catalogDeliveryDate || fallbackDeliveryDate;

    const stepsFieldName = useMemo(() => {
        for (const section of formFieldList) {
            const found = section.fields?.find((field) => field.type === 'steps');
            if (found) return found.name;
        }
        return null;
    }, [formFieldList]);

    const { data: productionStepsResponse } = useQuery({
        queryKey: ['getProductionSteps'],
        queryFn: getProductionSteps,
        staleTime: 1000 * 60,
        enabled: Boolean(stepsFieldName),
    });
    const productionStepsList = useMemo(
        () => (Array.isArray(productionStepsResponse?.data?.data) ? productionStepsResponse.data.data : []),
        [productionStepsResponse]
    );
    const productionStepNames = useMemo(
        () => mapProductionStepNames(productionStepsList),
        [productionStepsList]
    );
    const activeProductionSteps = useMemo(
        () => productionStepsList.filter((step) => step.is_active),
        [productionStepsList]
    );
    const defaultStepIds = useMemo(() => (
        (activeProductionSteps.length
            ? activeProductionSteps
            : Object.keys(fallbackStepNames).map((id) => ({ id: Number(id) }))
        ).map((step) => Number(step.id))
    ), [activeProductionSteps]);
    const { control, handleSubmit, setValue, getValues, watch } = useForm({
        defaultValues: {
            delivery_date: defaultDeliveryDate,
        }
    });

    const [profileImage, setProfileImage] = useState();
    const [images, setImages] = useState([]);
    const [stones, setStones] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [stoneModalOpen, setStoneModalOpen] = useState(false);
    const [stoneModalMode, setStoneModalMode] = useState("add");
    const [stoneModalInitialData, setStoneModalInitialData] = useState(null);
    const [stoneModalProps, setStoneModalProps] = useState(null);

    const openStoneModal = (props, mode = "add", index = null) => {
        setStoneModalProps(props);
        setStoneModalMode(mode);
        if (mode === "edit" || mode === "details") {
            const stoneData = stones[index];
            setStoneModalInitialData({
                ...stoneData,
                stone: {
                    value: stoneData.stone_id,
                    label: stoneData.stone_name
                },
                variation: {
                    value: stoneData.variation_id,
                    label: stoneData.variation_name,
                    price: stoneData.price
                },
                index: index
            });
        } else {
            setStoneModalInitialData(null);
        }
        setStoneModalOpen(true);
    };
    const closeStoneModal = () => {
        setStoneModalOpen(false);
        setStoneModalInitialData(null);
        setStoneModalProps(null);
    };

    const handleOpenStoneModal = (props) => openStoneModal(props, "add");
    const handleShowStoneUpdate = (props, index) => openStoneModal(props, "edit", index);
    const handleShowStoneDetails = (props, index) => openStoneModal(props, "details", index);

    const handleStoneModalSubmit = (data) => {
        if (stoneModalMode === "add") {
            handleAddStone(data);
        } else if (stoneModalMode === "edit") {
            handleUpdateStone(data);
        }
        closeStoneModal();
    };

    const handleCEPChange = async (cepValue) => {
        if (cepValue?.length === 10 && !details) {

            const addressData = await getVIACEPAddressByCEP(cepValue);

            if (addressData) {
                setValue('address', addressData.data.logradouro);
                setValue('neighborhood', addressData.data.bairro);
                setValue('city', addressData.data.localidade);
                setValue('state', addressData.data.uf);
            };
        };
    };

    useEffect(() => {
        if (initialData) {

            Object.keys(initialData).forEach((key) => {
                setValue(key, initialData[key]);
            });

            setProfileImage(initialData?.image);
            setImages(initialData?.images || []);
            setStones(initialData?.stones || [])
        };
    }, [initialData, setValue]);

    useEffect(() => {
        if (details || !stepsFieldName) return;
        const current = getValues(stepsFieldName);
        if (Array.isArray(current) && current.length > 0) return;
        if (defaultStepIds.length) {
            setValue(stepsFieldName, defaultStepIds);
        }
    }, [details, stepsFieldName, defaultStepIds, getValues, setValue]);

    useEffect(() => {
        if (details || initialData?.delivery_date) return;
        if (catalogDeliveryDate) {
            setValue('delivery_date', catalogDeliveryDate);
        }
    }, [catalogDeliveryDate, details, initialData, setValue]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const size = 128;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');
                    const aspectRatio = img.width / img.height;
                    let drawWidth, drawHeight, offsetX, offsetY;

                    if (aspectRatio > 1) {
                        drawWidth = size * aspectRatio;
                        drawHeight = size;
                        offsetX = -(drawWidth - size) / 2;
                        offsetY = 0;
                    } else {
                        drawWidth = size;
                        drawHeight = size / aspectRatio;
                        offsetX = 0;
                        offsetY = -(drawHeight - size) / 2;
                    };

                    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                    const base64Image = canvas.toDataURL('image/jpeg');
                    setValue("image", base64Image);
                    setProfileImage(base64Image);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        };
    };

    const handleAddStone = (data) => {
        const flattenedStone = {
            stone_id: data.stone?.value || data.stone,
            stone_name: data.stone?.label || data.stone,
            variation_id: data.variation?.value || data.variation,
            variation_name: data.variation?.label || data.variation,
            price: data.variation?.price || data.price,
            quantity: data.quantity,
            observation: data.observation
        };
        setStones((prevStones) => [...prevStones, flattenedStone]);
    };

    const handleRemoveStone = (index) => {
        setStones((prevStones) => prevStones.filter((_, i) => i !== index));
    };

    const handleUpdateStone = (data) => {
        handleRemoveStone(data.index);
        const flattenedStone = {
            stone_id: data.stone?.value || data.stone,
            stone_name: data.stone?.label || data.stone,
            variation_id: data.variation?.value || data.variation,
            variation_name: data.variation?.label || data.variation,
            price: data.variation?.price || data.price,
            quantity: data.quantity,
            observation: data.observation
        };
        setStones((prevStones) => [...prevStones, flattenedStone]);
    };

    const handleOpenFullScreenImage = (event) => {
        setImage(event.target.currentSrc);
        setIsImage(true);
        handleOpen();
    };

    const AvatarImageManagement = () => {
        return avatarUpload && (
            <Grid sx={{ backgroundImage: `url(${backgroundImage})`, backgroundPosition: 'center', borderRadius: 2, p: 2, display: 'flex', justifyContent: 'left' }} size={{ xs: 12 }} display="flex" justifyContent="center">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar src={profileImage} sx={{ width: 80, height: 80, mb: '1rem' }} />
                    {!details && (
                        <Button variant="contained" component="label" startIcon={<FaPen size={16} />}>
                            {getValues("image") ? "Editar" : "Adicionar"}
                            <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                        </Button>
                    )}
                </Box>
            </Grid>
        );
    };

    const handleMultipleImageUpload = (e) => {

        if (images.length >= 3) return;

        const file = e.target.files[0];

        if (file) {

            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const size = 500;
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext('2d');
                    const aspectRatio = img.width / img.height;
                    let drawWidth, drawHeight, offsetX, offsetY;

                    if (aspectRatio > 1) {

                        drawWidth = size * aspectRatio;
                        drawHeight = size;
                        offsetX = -(drawWidth - size) / 2;
                        offsetY = 0;

                    } else {

                        drawWidth = size;
                        drawHeight = size / aspectRatio;
                        offsetX = 0;
                        offsetY = -(drawHeight - size) / 2;

                    };

                    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
                    const base64Image = canvas.toDataURL('image/jpeg');
                    setImages((prevImages) => [...prevImages, base64Image]);
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
        };
    };

    const handleRemoveImage = (index) => {

        setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    };

    const MultiImageManagement = () => {

        return multiImageUpload && (
            <Grid gap={2} container component={Card} sx={{ p: 2, justifyContent: 'left' }} size={{ xs: 12 }} display="flex" justifyContent="center">
                {images.map((src, index) => (
                    <Grid key={index} size={{ xs: 6, lg: 1.5 }} sx={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
                        {!details && <IconButton
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleRemoveImage(index)}
                            sx={{ position: 'absolute', top: 4, right: 4 }}
                        >
                            <FaTrash />
                        </IconButton>}
                        <Box component={'img'} sx={{ borderRadius: 2, width: '100%', cursor: 'pointer' }} src={src} onClick={handleOpenFullScreenImage} />
                    </Grid>
                ))}
                {(images.length < 3 && !details) && (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }} size={{ xs: 6, lg: 1.5 }}>
                        <Button variant="contained" component="label" startIcon={<FaPen size={16} />}>
                            Incluir imagem
                            <input type="file" accept="image/*" hidden onChange={handleMultipleImageUpload} />
                        </Button>
                    </Grid>
                )}
            </Grid>
        );
    };

    const onSubmitHandler = async (data) => {

        try {
            if (images.length >= 1) {
                data.images = images;
            } else {
                data.images = [];
            };

            data.stones = stones;
            if (!data.extras) {
                data.extras = [];
            }
            await onSubmit(data);
        } finally {
            setIsSubmitting(false);
        };
    };


    const FormField = ({ field, control, details, getValues }) => {
        const [isLoadingOptions, setIsLoadingOptions] = useState(false);
        const [options, setOptions] = useState(field.options || []);

        const dependentValue = field.dependsOn ? watch(field.dependsOn) : null;

        useEffect(() => {
            if (!field.dependsOn) return;

            const fetchOptions = async () => {
                
                setIsLoadingOptions(true);

                try {
                    if (dependentValue && dependentValue !== '' && dependentValue !== ' ' && dependentValue !== 0) {
                        if (typeof field.dynamicOptions === 'function') {
                            const response = await field.dynamicOptions(dependentValue.value);
                            const formattedOptions = response.data.data.map(item => ({
                                label: item.name,
                                value: parseInt(item.id),
                                ...(item.price !== undefined && {
                                    price: parseMoneyToCents(item.price)
                                }),
                            }));
                            setOptions(formattedOptions);
                        }
                    } else {
                        setOptions([]);
                        if (getValues(field.name)) setValue(field.name, null);
                    }
                } catch (error) {
                    console.error('Error loading dynamic options:', error);
                    setOptions([]);
                } finally {
                    setIsLoadingOptions(false);
                }
            };
            fetchOptions();
        }, [dependentValue]);

        return (
            <Controller
                name={field.name}
                control={control}
                defaultValue={field.defaultValue || ''}
                render={({ field: controllerField }) => {
                    switch (true) {
                        case field.type === 'reset_password':
                            return (
                                <Button component={Link} to="/redefinir-senha" startIcon={<FaLock />} variant='contained' color='secondary'>Redefinir senha</Button>
                            );
                        case field.mask === 'phone':
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    variant="filled"
                                    label={field.label}
                                    required={field.required}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    InputProps={{
                                        inputComponent: PhoneMaskCustom,
                                        inputProps: { name: field.name }
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                />
                            );
                        case field.mask === 'cpf_cnpj':
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    variant="filled"
                                    label={field.label}
                                    required={field.required}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    InputProps={{
                                        inputComponent: CpfCnpjMaskCustom,
                                        inputProps: { name: field.name }
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                />
                            );
                        case field.mask === 'currency':
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    variant="filled"
                                    label={field.label}
                                    required={field.required}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    InputProps={{
                                        inputComponent: CurrencyMaskCustom,
                                        inputProps: { name: field.name },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                R$
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                />
                            );
                        case field.mask === 'zipcode':
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    variant="filled"
                                    label={field.label}
                                    required={field.required}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    InputProps={{
                                        inputComponent: ZipCodeMaskCustom,
                                        inputProps: {
                                            name: field.name,
                                            onComplete: (value) => handleCEPChange(value)
                                        }
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                />
                            );
                        case field.mask:
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    variant="filled"
                                    label={field.label}
                                    required={field.required}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    InputProps={{
                                        inputComponent: TextMaskCustom,
                                        inputProps: {
                                            name: field.name,
                                            mask: field.mask
                                        }
                                    }}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                />
                            );
                        case field.type === 'select':
                            return (
                                <FormControl disabled={details || field.disabled || isLoadingOptions} fullWidth required={field.required}>
                                    <InputLabel>{field.label}</InputLabel>
                                    <Select
                                        {...controllerField}
                                        disabled={details || field.disabled || isLoadingOptions}
                                        fullWidth
                                        variant="filled"
                                        label={field.label}
                                        required={field.required}
                                        value={controllerField.value ?? null}
                                        defaultValue={null}
                                        MenuProps={{ style: { maxHeight: 250 } }}
                                        sx={{
                                            "& .MuiInputBase-input.Mui-disabled": {
                                                WebkitTextFillColor: "#000000",
                                            },
                                        }}
                                    >
                                        <MenuItem value={null}><i>Selecionar</i></MenuItem>
                                        {options?.map((opt, index) => (
                                            <MenuItem key={index} value={opt.value} name={opt.label}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        case field.type === 'switch' && field.name === 'has_catalog':
                            return (
                                <FormControl fullWidth>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                        <Switch disabled={details || field.disabled || isLoadingOptions} {...controllerField} checked={getValues('has_catalog')} color="secondary" />
                                        <Button
                                            disabled={!getValues('has_catalog') || field.disabled || isLoadingOptions}
                                            sx={{ ml: 1 }}
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => copyToClipBoard(`http://inovartgold.com.br/catalogo-parceiro/${getValues('catalog_slug')}`)}
                                            startIcon={<FaRegCopy />}
                                        >
                                            Copiar Link de catálogo
                                        </Button>
                                    </Box>
                                </FormControl>
                            );
                        case field.type === 'switch' && field.name === 'is_active':
                            return (
                                <FormControlLabel control={
                                    <Switch checked={getValues('is_active')} disabled={details || field.disabled} {...controllerField} color="secondary" />
                                } label={field.label}
                                    labelPlacement="top"
                                />
                            );
                        case field.type === 'check':
                            return (
                                <FormControlLabel sx={{ height: '100%' }} control={
                                    <Checkbox checked={getValues(field.name)} disabled={details || field.disabled} {...controllerField} color="primary" />
                                } label={field.label}
                                    labelPlacement="start"
                                />
                            );
                        case field.type === 'steps': {
                            const currentSteps = Array.isArray(getValues(field.name)) ? getValues(field.name) : [];
                            const selectableSteps = (activeProductionSteps.length
                                ? activeProductionSteps
                                : Object.entries(fallbackStepNames).map(([id, name]) => ({ id: Number(id), name }))
                            );
                            const unusedSteps = selectableSteps.filter((step) => !currentSteps.includes(Number(step.id)));

                            return (
                                <Box display={'flex'} flexDirection={'column'}>
                                    <Typography textAlign={'left'}>
                                        Passos de Produção
                                    </Typography>
                                    {!details && !field.disabled && presets.length > 0 && (
                                        <FormControl sx={{ minWidth: 220, mt: 1, maxWidth: 320 }}>
                                            <Select
                                                value=""
                                                displayEmpty
                                                size="small"
                                                onChange={(e) => {
                                                    const preset = presets.find((item) => String(item.id) === String(e.target.value));
                                                    if (preset?.steps?.length) {
                                                        setValue(field.name, preset.steps.map((step) => Number(step)));
                                                    }
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    <em>Aplicar preset de fluxo</em>
                                                </MenuItem>
                                                {presets.map((preset) => (
                                                    <MenuItem key={preset.id} value={preset.id}>
                                                        {preset.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )}
                                    <Stack mt={1} spacing={1} direction={'row'} alignItems={'center'} flexWrap="wrap" useFlexGap>
                                        {currentSteps.map((step, index) => (
                                            <Chip
                                                key={`${step}-${index}`}
                                                label={productionStepNames[step] || fallbackStepNames[step] || `Etapa ${step}`}
                                                variant="outlined"
                                                onDelete={(details || field.disabled) ? null : () => {
                                                    const nextSteps = getValues(field.name).filter((_, i) => i !== index);
                                                    setValue(field.name, nextSteps);
                                                }}
                                            />
                                        ))}
                                        {!details && !field.disabled && unusedSteps.length > 0 && (
                                            <FormControl sx={{ minWidth: 120 }}>
                                                <Select
                                                    value=""
                                                    onChange={(e) => {
                                                        const selectedStep = parseInt(e.target.value, 10);
                                                        setValue(field.name, [...getValues(field.name), selectedStep]);
                                                    }}
                                                    sx={{
                                                        borderRadius: 10,
                                                    }}
                                                    displayEmpty
                                                    size="small"
                                                >
                                                    <MenuItem value="" disabled>
                                                        <em>Adicionar próxima etapa</em>
                                                    </MenuItem>
                                                    {selectableSteps.map((step) => (
                                                        <MenuItem
                                                            key={step.id}
                                                            value={step.id}
                                                            disabled={currentSteps.includes(Number(step.id))}
                                                        >
                                                            {step.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Stack>
                                </Box>
                            );
                        }
                        case field.type === 'extras':
                            return (
                                <Autocomplete
                                    multiple
                                    disabled={details || field.disabled}
                                    options={field.options || []}
                                    getOptionLabel={field.getOptionLabel || ((option) => option.label)}
                                    value={getValues(field.name) || []}
                                    onChange={(_, newValue) => {
                                        setValue(field.name, newValue, { shouldValidate: true });
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={field.label}
                                            variant="outlined"
                                            placeholder="Selecione os serviços extras"
                                        />
                                    )}
                                />
                            );
                        case field.type === 'stones':

                            return (
                                <Grid spacing={2} container width={'100%'}>
                                    {stones.map((stone, index) => (
                                        <Grid key={index} item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} >
                                            <Card elevation={2} sx={{ p: '1.5rem', bgcolor: '#FAFAFA' }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                                    <Typography fontWeight={600}>{stone.stone_name}</Typography>
                                                    <Stack direction="row" spacing={1}>
                                                        <IconButton color="primary" onClick={() => handleShowStoneDetails(field.modal_props, index)}>
                                                            <FaMagnifyingGlass size={16} />
                                                        </IconButton>
                                                        {!details && !field.disabled && (
                                                            <>
                                                                <IconButton color="primary" onClick={() => handleShowStoneUpdate(field.modal_props, index)}>
                                                                    <FaPen size={16} />
                                                                </IconButton>
                                                                <IconButton onClick={() => handleRemoveStone(index)}>
                                                                    <FaTrashCan size={16} color="red" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </Stack>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column', mt: '1rem' }}>
                                                    <Typography textAlign={'left'}>
                                                        <strong>{stone.quantity}</strong> unidades de <strong>{stone.variation_name}</strong>
                                                    </Typography>
                                                    {stone.observation && (
                                                        <Typography mt={'.5rem'} fontSize={'.875rem'} color="grey">
                                                            "{stone.observation}"
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box display={'flex'} justifyContent={'end'}>
                                                    <Typography fontWeight={700}>
                                                        {formatCurrency(stone.quantity * stone.price)}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    ))}

                                    {!details && !field.disabled && <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            disabled={field.disabled}
                                            onClick={() => handleOpenStoneModal(field.modal_props)}
                                            variant="contained"
                                            startIcon={<FaPlus />}
                                            fullWidth
                                            sx={{ height: '100%' }}
                                        >
                                            Adicionar pedra
                                        </Button>
                                    </Grid>}
                                </Grid>
                            );
                        case field.type === 'preview_pdf': {
                            // Melhoria: componente reutilizável para preview de PDF
                            const pdfMap = {
                                has_address_proof: {
                                    label: "Preview de PDF de Endereço",
                                    file: "address_doc"
                                },
                                has_cnpj_certificate: {
                                    label: "Preview de PDF de CNPJ",
                                    file: "cnpj_doc"
                                }
                            };

                            const value = getValues(field.name);
                            const id = getValues('id');
                            const pdfInfo = pdfMap[field.name];

                            if (value && pdfInfo && id) {
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                                        <Typography>{pdfInfo.label}</Typography>
                                        <Box
                                            component="iframe"
                                            src={`http://inovartgold.com.br/assets/customer-files/${id}_${pdfInfo.file}.pdf`}
                                            width="100%"
                                            height="300px"
                                            sx={{ borderRadius: 2, border: '1px solid #E0E0E0' }}
                                        />
                                    </Box>
                                );
                            }
                            return null;
                        }
                        case field.type === 'select_object':
                            return (
                                <Autocomplete
                                    options={field.options ?? options}
                                    getOptionLabel={(option) => option.label}
                                    value={getValues(field.name) || null}
                                    onChange={(_, newValue) => {
                                        setValue(field.name, newValue, { shouldValidate: true });
                                    }}
                                    renderInput={(params) => <TextField variant='filled' {...params} label={field.label} />}
                                    disabled={details || field.disabled}
                                />
                            );
                        case field.type === 'date_time_picker':
                            const isDisabled = details || field.disabled;
                            return (
                                <DateTimePicker
                                    {...controllerField}
                                    sx={{
                                        width: '100%',
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                    required={field.required}
                                    value={controllerField.value ? dayjs(controllerField.value) : defaultDeliveryDate}
                                    label={field.label}
                                    disabled={isDisabled}
                                    minDate={isDisabled ? undefined : currentDate}
                                    onChange={value => controllerField.onChange(value)}
                                    shouldDisableDate={(date) => {
                                        const dayOfWeek = date.day();
                                        return dayOfWeek === 0 || dayOfWeek === 6;
                                    }}
                                    dayOfWeekFormatter={(weekday) => `${weekday.format('dd')}.`}
                                    slotProps={{
                                        textField: {
                                            variant: 'filled',
                                            helperText: field.helpertext,
                                            required: field.required,
                                            fullWidth: true,
                                        }
                                    }}
                                />
                            );
                        case field.type === 'payment_methods_display':
                            const paymentMethods = getValues(field.name) || [];
                            const totalValue = paymentMethods.reduce((sum, method) => sum + (parseFloat(method.price) || 0), 0);

                            return (
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        {field.label}
                                    </Typography>
                                    {paymentMethods.length > 0 ? (
                                        <Stack spacing={2}>
                                            {paymentMethods.map((method, index) => (
                                                <Card key={index}>
                                                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box>
                                                            <Typography textAlign={'left'} fontWeight="bold">
                                                                {method.payment_name}
                                                            </Typography>
                                                            {method.observation && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {method.observation}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        <Typography color="primary" fontWeight="bold">
                                                            {formatCurrency(method.price)}
                                                        </Typography>
                                                    </Box>
                                                </Card>
                                            ))}
                                            <Card sx={{ p: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="h6" fontWeight="bold">
                                                        Total
                                                    </Typography>
                                                    <Typography color="success" variant="h6" fontWeight="bold">
                                                        {formatCurrency(totalValue)}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        </Stack>
                                    ) : (
                                        <Typography color="text.secondary" fontStyle="italic">
                                            Nenhuma forma de pagamento registrada
                                        </Typography>
                                    )}
                                </Box>
                            );
                        default:
                            return (
                                <TextField
                                    {...controllerField}
                                    fullWidth
                                    multiline={field.multiline}
                                    rows={field.rows}
                                    variant="filled"
                                    label={field.label}
                                    type={field.type || 'text'}
                                    required={field.required}
                                    InputLabelProps={field.type === 'date' ? { shrink: true } : {}}
                                    disabled={details || field.disabled || isLoadingOptions}
                                    helperText={field.helpertext}
                                    sx={{
                                        "& .MuiInputBase-input.Mui-disabled": {
                                            WebkitTextFillColor: "#000000",
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: field.end_adornment && <InputAdornment position="end">{field.end_adornment}</InputAdornment>,
                                        startAdornment: field.start_adornment && <InputAdornment position="start">{field.start_adornment}</InputAdornment>,
                                    }}
                                />
                            );
                    }
                }}
            />
        );
    };

    const CustomFormBuilder = () => {
        return formFieldList?.map((block, index) => {

            const filteredFields = details
                ? block.fields.filter(field => {
                    const fieldValue = getValues(field.name);
                    return fieldValue !== "" && fieldValue !== 0 && fieldValue !== null && fieldValue !== '0' && fieldValue !== '0.00' && fieldValue?.length !== 0;
                })
                : block.fields;

            if (details && filteredFields.length === 0) return null;

            return (
                <Grid key={index} size={{ xs: 12 }}>
                    <Card sx={{ p: '2rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
                        <Typography fontSize={20} mb={'1.5rem'}>{block.title}</Typography>
                        <Grid container spacing={2} width={'100%'}>
                            {filteredFields.map((field, index) => (
                                <Grid key={index} size={field.fullWidth ? { xs: 12 } : { xs: 12, sm: 6, md: 4 }}>
                                    <FormField field={field} control={control} details={details} getValues={getValues} />
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </Grid>
            );
        });
    };


    const PageLayout = ({ children }) => {
        return (
            <Grid component="form" onSubmit={handleSubmit(onSubmitHandler)} container spacing={2}>
                {children}
            </Grid>
        );
    };

    const StonesDialog = () => (
        <Dialog open={stoneModalOpen} onClose={closeStoneModal} maxWidth="md" fullWidth>
            <DialogTitle>
                {stoneModalMode === "add" && "Inclusão de Pedra"}
                {stoneModalMode === "edit" && "Edição de Pedra"}
                {stoneModalMode === "details" && "Detalhes de Pedra"}
            </DialogTitle>
            <DialogContent>
                {stoneModalProps && (
                    <CustomFormPage
                        details={stoneModalMode === "details"}
                        initialData={stoneModalInitialData}
                        onSubmit={handleStoneModalSubmit}
                        onCancel={closeStoneModal}
                        formFieldList={stoneModalProps.fields}
                        submitButtonText={stoneModalMode === "details" ? undefined : "Salvar"}
                        cancelButtonText="Cancelar"
                    />
                )}
            </DialogContent>
            {stoneModalMode !== "details" && (
                <DialogActions>
                    <MuiButton onClick={closeStoneModal}>Cancelar</MuiButton>
                    {/* O botão de salvar está dentro do CustomFormPage */}
                </DialogActions>
            )}
        </Dialog>
    );

    if (isLoading || (details && !initialData)) return <FormLoading />;

    return (
        <>
            <PageLayout>
                <MultiImageManagement />
                <AvatarImageManagement />
                <CustomFormBuilder />
                <FormPageNavigation
                    noAction={noAction}
                    production={production}
                    updateProduction={updateProduction}
                    details={details}
                    verification={verification}
                    isSubmitting={isSubmitting}
                    submitButtonText={submitButtonText}
                    cancelButtonText={cancelButtonText}
                    onCancel={onCancel}
                    fixedActions={fixedActions}
                />
            </PageLayout>
            <StonesDialog />
        </>

    );
};